import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Plug, Plus, Copy, Trash2, AlertTriangle, ShieldCheck } from "lucide-react";
import { toUserFriendlyError } from "@/lib/error-messages";

const PUBLIC_URL = typeof window !== "undefined" ? window.location.origin : "";

function formatDate(value: string | Date | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function McpTokensPanel() {
  const utils = trpc.useUtils();
  const { data: tokens = [], isLoading } = trpc.mcpTokens.list.useQuery();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  // Valor em claro do token recém-criado. Só existe em memória, some no reload
  // — o servidor guarda apenas o hash e não consegue reexibi-lo.
  const [freshToken, setFreshToken] = useState<string | null>(null);

  const createMut = trpc.mcpTokens.create.useMutation({
    onSuccess: (res) => {
      setFreshToken(res.token);
      setShowForm(false);
      setName("");
      utils.mcpTokens.list.invalidate();
      toast.success("Token criado! Copie agora — ele não será exibido de novo.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const revokeMut = trpc.mcpTokens.revoke.useMutation({
    onSuccess: () => {
      utils.mcpTokens.list.invalidate();
      toast.success("Token revogado.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  // Aplicativos que passaram pela tela de autorização (OAuth), em vez de usar
  // um token colado à mão.
  const { data: grants = [] } = trpc.mcpTokens.listOauthGrants.useQuery();

  const revokeGrantMut = trpc.mcpTokens.revokeOauthGrant.useMutation({
    onSuccess: () => {
      utils.mcpTokens.listOauthGrants.invalidate();
      toast.success("Acesso do aplicativo revogado.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const activeGrants = grants.filter((g) => !g.isRevoked);

  function copy(text: string, label: string) {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  }

  const connectCommand = freshToken
    ? `claude mcp add --transport http leadconversa ${PUBLIC_URL}/mcp --header "Authorization: Bearer ${freshToken}"`
    : "";

  return (
    <>
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Plug className="h-4 w-4 text-primary" />
              Tokens de acesso MCP
            </CardTitle>
            <CardDescription className="mt-1">
              Conecte o Claude ao LeadConversa. O token age <strong>com as suas
              permissões</strong> — nem mais, nem menos.
            </CardDescription>
          </div>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => {
              setShowForm((v) => !v);
              setFreshToken(null);
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            Novo token
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {freshToken && (
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm">
                Copie agora. Este token não será exibido novamente — o servidor
                guarda apenas um hash dele.
              </p>
            </div>
            <div className="flex gap-2">
              <Input readOnly value={freshToken} className="font-mono text-xs" />
              <Button
                size="sm"
                variant="outline"
                onClick={() => copy(freshToken, "Token")}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Comando para conectar no Claude Code</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={connectCommand}
                  className="font-mono text-xs"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copy(connectCommand, "Comando")}
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {showForm && (
          <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
            <div className="space-y-1.5">
              <Label>Nome do token *</Label>
              <Input
                placeholder="Ex: Claude no meu notebook"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
              />
              <p className="text-xs text-muted-foreground">
                Serve só para você identificar depois. Validade de 90 dias.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={!name.trim() || createMut.isPending}
                onClick={() => createMut.mutate({ name: name.trim(), ttlDays: 90 })}
              >
                {createMut.isPending ? "Criando..." : "Criar token"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-muted-foreground text-center py-6">Carregando...</p>
        ) : tokens.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            Nenhum token criado. Clique em "Novo token" para conectar o Claude.
          </p>
        ) : (
          <div className="space-y-2">
            {tokens.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between border rounded-lg p-3 gap-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate">{t.name}</span>
                    <code className="text-xs text-muted-foreground font-mono">
                      lcmcp_{t.tokenPrefix}…
                    </code>
                    {t.isRevoked && <Badge variant="destructive">Revogado</Badge>}
                    {!t.isRevoked && t.isExpired && <Badge variant="outline">Expirado</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Último uso: {formatDate(t.lastUsedAt)} · Expira em{" "}
                    {formatDate(t.expiresAt)}
                  </p>
                </div>
                {!t.isRevoked && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive shrink-0"
                    disabled={revokeMut.isPending}
                    onClick={() => revokeMut.mutate({ id: t.id })}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>

    {/*
      Só aparece depois que existe algo para mostrar. Um card vazio explicando
      um fluxo que a pessoa talvez nunca use só competiria com o de cima.
    */}
    {activeGrants.length > 0 && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Aplicativos conectados
          </CardTitle>
          <CardDescription className="mt-1">
            Aplicativos que você autorizou a acessar o LeadConversa em seu nome.
            Revogar corta o acesso na hora.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {activeGrants.map((g) => (
            <div
              key={g.id}
              className="flex items-center justify-between border rounded-lg p-3 gap-3"
            >
              <div className="min-w-0">
                <span className="text-sm font-medium truncate">
                  {g.clientName || "Aplicativo externo"}
                </span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Autorizado em {formatDate(g.grantedAt)} · Último uso:{" "}
                  {formatDate(g.lastUsedAt)}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive shrink-0"
                disabled={revokeGrantMut.isPending}
                onClick={() => revokeGrantMut.mutate({ id: g.id })}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    )}
    </>
  );
}
